import { useState } from 'react'
import { useAuth } from '@/lib/AuthContext'
import { useSubmitSuggestion, useIdeaBoardCategoryNames } from '@/idea-board/hooks/useIdeaBoardData'
import IdeaAttachmentDropzone from '@/idea-board/components/IdeaAttachmentDropzone'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CheckCircle2, Send } from 'lucide-react'

export default function SubmitIdeaDialog({ open, onOpenChange }) {
  const { user, profile } = useAuth()
  const submitMutation = useSubmitSuggestion()
  const categoryNames = useIdeaBoardCategoryNames()
  const [submitted, setSubmitted] = useState(false)
  const [form, setForm] = useState({ title: '', description: '', category: '' })
  const [files, setFiles] = useState([])
  const [submitError, setSubmitError] = useState(null)

  const resetForm = () => {
    setForm({ title: '', description: '', category: '' })
    setFiles([])
    setSubmitError(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitError(null)
    try {
      await submitMutation.mutateAsync({
        title: form.title,
        description: form.description,
        category: form.category,
        user,
        profile,
        files,
      })
      setSubmitted(true)
      setTimeout(() => {
        setSubmitted(false)
        resetForm()
        onOpenChange(false)
      }, 1800)
    } catch (err) {
      setSubmitError(err.message || 'Failed to submit idea')
    }
  }

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen && !submitMutation.isPending) {
      resetForm()
    }
    onOpenChange(nextOpen)
  }

  const isValid = form.title.trim() && form.description.trim() && form.category

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        {submitted ? (
          <div className="flex flex-col items-center py-10 gap-4">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold">Idea submitted!</h3>
            <p className="text-sm text-muted-foreground text-center">
              Your idea is now on the board for everyone to see and vote on.
            </p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-semibold">Submit an Idea</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-1">
              <div className="space-y-1.5">
                <Label>Title</Label>
                <Input
                  placeholder="What's the idea in one line?"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  maxLength={120}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Description</Label>
                <Textarea
                  placeholder="Describe the problem and your proposed solution..."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="min-h-[110px] resize-none"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select
                  value={form.category}
                  onValueChange={(val) => setForm({ ...form, category: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Pick a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categoryNames.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Attachments (optional)</Label>
                <IdeaAttachmentDropzone
                  files={files}
                  onChange={setFiles}
                  disabled={submitMutation.isPending}
                />
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
                Your name won&apos;t appear publicly, but admins can see who submitted suggestions
                if we move forward with an idea.
              </div>
              {submitError && <p className="text-xs text-destructive">{submitError}</p>}
              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold gap-2"
                disabled={!isValid || submitMutation.isPending}
              >
                <Send className="w-4 h-4" />
                {submitMutation.isPending ? 'Submitting...' : 'Submit Idea'}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
